import dayjs from "dayjs";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import { numberToWords } from "./numberToWords";
import { getOAuthHeaders } from "../constants/oAuthValidation";
import printController from "../lib/printer/printController";
import CWKOTPrinter from "../lib/printer/CWKotPrinter";
import updateOfdStatus from "./updateOfdStatus";
import { salesHistoryDetails } from "../services";
import validityCheck from "./validityCheck";
import { claimPrintOnce, printGuardReady } from "./printGuard";
import useAuthStore from "../stores/useAuthStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import db from "../database";
import { getIsInterState } from "./taxUtils";
import { groupItemsByTaxRate } from "../components/PointOfSale/Core/utils/taxCalculations";
import { trace } from "./debugTrace";
import { showPrintFailure } from "../lib/printer/printFailureNotice";

const getSalesHistoryDetails = async (order_id, selectedOrderStatus, forPrinting = false, channelId, manualprint = false) => {
  // Common variables
  const serverUrl = process.env.REACT_APP_serverUrl;
  // TSK-5289: legacy env value, or the Till Template flag.
  const posConfig = usePOSConfigStore.getState().posConfig;
  const disableAggregatorInvoicePrint = process.env.REACT_APP_DISABLE_AGGREGATOR_INVOICE_PRINT === "true" || posConfig?.disableAggregatorInvoicePrint === "Y";
  const disableAggregatorKOTPrint = process.env.REACT_APP_DISABLE_AGGREGATOR_KOT_PRINT === "true" || posConfig?.disableAggregatorKOTPrint === "Y";
  const authHeaders = getOAuthHeaders();
  const userData = useAuthStore.getState().userData;
  const tillData = useAuthStore.getState().tillData;
  const tillValue = useAuthStore.getState().tillValue;
  const precision = tillData?.tillAccess?.csBunit?.currencies[0]?.prcPrecision || 2;

  let transformedOrder = null;

  try {

    // Validate inputs
    if (!order_id) {
      console.error("Invalid order_id: ", order_id);
      return null;
    }
    if (!serverUrl || !authHeaders.access_token) {
      console.error("Missing serverUrl or authHeaders: ", { serverUrl, authHeaders });
      return null;
    }

    // BUG-1971: claim the print guard BEFORE the network fetch below, not after it.
    // handleAutoAccept and the WS-driven handleOrderUpdate (App.js) both call this
    // function for the same order_id, often within milliseconds of each other. The
    // guard used to run after `await salesHistoryDetails(data)` -- since both calls
    // do that fetch independently, both could reach the guard while neither had
    // claimed yet, and both would print. claimPrintOnce is a single synchronous
    // check-and-set (no internal await), so calling it here, before either call
    // starts its own fetch, is genuinely atomic and closes that window -- unlike
    // a separately-awaited check then set, which can still interleave when two
    // callers are started back-to-back (e.g. Promise.all).
    const isAutoPrintRequest = forPrinting && !manualprint;
    // BUG-2373: let the IndexedDB copy of the guard (survives localStorage.clear
    // on logout/till close) merge in first. Awaited BEFORE the synchronous claim,
    // so concurrent callers still resolve atomically inside claimPrintOnce.
    if (isAutoPrintRequest) await printGuardReady;
    if (isAutoPrintRequest && !claimPrintOnce(order_id)) {
      console.warn(`[PrintGuard] Duplicate print attempt suppressed before fetch for order ${order_id}`);
      return null;
    }

    const data = {
      query: `query {salesHistoryDetails(
                    orderId: "${order_id}"
                  ) {
                    sOrderID
                    b2bOrder
                    created
                    createdby
                    updated
                    updatedby
                    deliveryDate
                    documentno
                    invoiceNo
                    invoiceId
                    invoiceStatus
                    invoiceDate
                    dateordered
                    cwrProductQty
                    orderTime
                    taxamt
                    grosstotal
                    discAmount
                    layAway
                    isReturn
                    paid
                    print
                    isBilled
                    csUser {
                      user
                    }
                    csBUnit {
                      csBunitId
                      name
                    }
                    csbUnitLocation {
                      fulladdress
                    }
                    cwrB2cCustomer {
                      cwrCustomerId
                      sPriceListId
                      code
                      name
                      mobileNo
                      pincode
                      email
                      retlLoyaltyBalance
                      iscredit
                      loyaltyLevelId
                      creditBalance
                      firstName
                      lastName
                      gender
                      taxId
                      birthday
                      anniversaryDate
                      b2cRegisteredstoreId
                      cardNumber
                      regType
                      validity
                      b2bCustomer
                      balancepoints
                      creditused
                      creditLimit
                      creditApproachPercentage
                      priceListName
                      customerList
                      loyaltyLevel {
                        cwrLoyaltyLevelId
                        name
                        accumulationRate
                        redemptionRate
                      }
                      cwrWallet {
                        wallet_Id
                        balance_Amt
                      }
                      sCustomer {
                        sCustomerID
                        customerCategory {
                          sCustomerCateforyId
                          value
                          name
                          description
                        }
                      }
                      b2cCustomerSegment {
                          cwrB2CCustomerSegmentId
                          name
                      }
                      metaData {
                        key
                        value
                     }
                    }
                    saleType {
                      cwrSaletypeId
                      name
                      value
                      enableKot
                    }
                    cwrTill {
                      cwrTillID
                      till
                    }
                    tablename
                    fboRder {
                      guests
                    }
                    saLesRep {
                      waitername
                    }
                    finReceiptPlan {
                      finReceiptPlanDetails {
                        amount
                        transactionId
                        cardType
                        cardNo
                        transactionTime
                        authorizationCode
                        cwrPaymentmethod {
                          cWRPaymentMethodID
                          finFinancialAccountId
                          finPaymentmethodId
                          integratedPayment
                          isloyalty
                          paymentProvider
                        }
                      }
                    }
                    line {
                      sOrderlineID
                      sOrderId
                      line
                      description
                      qty
                      deliveryDate
                      netlist
                      netunit
                      created
                      linetax
                      unittax
                      unitprice
                      linenet
                      linegross
                      grosslist
                      grossstd
                      returnline
                      returnQty
                      discount
                      mBatchId
                      product {
                        mProductId
                        name
                        value
                        upc
                        hsn
                        hsncode
                        imageurl
                        isManualQty
                        shortDescription
                        returnable
                        returnDays
                        productSegment
                      }
                      uom {
                        csUomId
                        name
                        decimal
                      }
                      tax {
                        csTaxID
                        name
                        rate
                      }
                      pricingRule {
                        mPricingrulesId
                        name
                      }
                      addons {
                        qty
                        price
                        name
                      }
                    }
                    metaData { key value }
                    externalId
                    externalId2
                    additionalCharges {
                      sOrderAdditionalChargesId
                      sOrderId
                      name
                      amount
                    }
                  }
                }`,
    };

    const response = await salesHistoryDetails(data);

    const orderData = response?.data?.data?.salesHistoryDetails?.[0];

    if (!orderData) {
      console.error(`No order data found for order_id: ${order_id}`);
      return null;
    }

    // TSK-2956 follow-up diagnostic: capture raw order/line tax fields to trace the
    // ₹2.30 gap between orderData.taxamt and the expected packing-excluded item tax.
    trace("SalesHistory", "orderData tax breakdown", {
      documentno: orderData.documentno,
      grosstotal: orderData.grosstotal,
      taxamt: orderData.taxamt,
      discAmount: orderData.discAmount,
      additionalCharges: orderData.additionalCharges,
      lines: (orderData.line || []).map((l) => ({
        name: l.product?.name,
        qty: l.qty,
        unitprice: l.unitprice,
        netunit: l.netunit,
        unittax: l.unittax,
        linegross: l.linegross,
        linenet: l.linenet,
        linetax: l.linetax,
        taxRate: l.tax?.rate,
      })),
    });

    if (forPrinting || manualprint) {
      const itemSummery = {
        decimalItems: { totalQty: 0, totalAmount: 0 },
        nonDecimalItems: { totalQty: 0, totalAmount: 0 },
        itemsQty: 0,
        items: 0,
      };

      const isInterState = getIsInterState({ customer: orderData.cwrB2cCustomer });
      const groupedItemsByTaxRate = groupItemsByTaxRate(orderData.line || [], precision, { isInterState, taxField: "linetax", netField: "linegross" });
      const allUoms = await db.productUom.toArray();
      const uomMap = new Map();
      for (const uom of allUoms) {
        if (!uomMap.has(uom.csUomId)) uomMap.set(uom.csUomId, []);
        uomMap.get(uom.csUomId).push(uom);
      }
      const lineItems = (orderData.line || []).map((pro) => {
        const isDecimal = pro.uom?.decimal === "Y" || false;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
        itemSummery.itemsQty += pro.qty;
        itemSummery.items += 1;
        itemSummery[summaryKey].totalQty += pro.qty;
        itemSummery[summaryKey].totalAmount += pro.linegross;
        const uomData = uomMap.get(pro.uom?.csUomId) || [];
        // Undiscounted lines: grossstd carries its own independent backend rounding that can
        // drift a paisa from the actual charged amount (e.g. grossstd=166.94 vs the true
        // linegross/qty=166.95), showing up as a Price/Amount mismatch on the receipt. Only
        // trust grossstd (the pre-discount price, per BUG-1558) when the line actually had a
        // discount — there it legitimately differs from netunit by design and the paisa-level
        // noise is immaterial. Otherwise use the exact nettotal-derived value.
        const preDiscountUnitPrice = pro.discount > 0 ? pro.grossstd || pro.netunit || 0 : pro.qty > 0 ? (pro.linegross || 0) / pro.qty : pro.grossstd || pro.netunit || 0;
        return {
          batchno: null,
          description: pro.description || "",
          discount: pro.discount ?? 0,
          discountName: pro.pricingRule?.name || "",
          imageurl: pro.product?.imageurl || "",
          isDecimal: isDecimal,
          isManualQty: pro.product?.isManualQty || false,
          restrictPosQtyEdit: pro.product?.restrictPosQtyEdit || "N",
          showAlternateUomInPos: pro?.product?.showAlternateUomInPos || "N",
          interTaxId: pro?.product?.interTaxId || "",
          interTaxRate: pro?.product?.interTaxRate || 0,
          interTaxName: pro?.product?.interTaxName || "",
          alternateUom: pro?.product?.alternateUom || null,
          companion: pro?.product?.companion || "N",
          deliveryDate: pro.deliveryDate ? dayjs(pro.deliveryDate).format("DD-MM-YYYY HH:mm:ss") : null,
          isReturn: false,
          mBatchId: pro?.mBatchId || null,
          giftCardType: null,
          mPricingruleId: pro.pricingRule?.mPricingrulesId || null,
          name: pro.product?.name || "",
          name2: "",
          linegross: pro?.linegross || 0,
          nettotal: pro.linegross || 0,
          primaryOrderLine: null,
          productId: pro.product?.mProductId || null,
          // netunit is the backend's tax-exclusive per-unit price (post-discount, what was
          // actually charged); unitprice is tax-inclusive for online/aggregator orders.
          // salePrice/sunitprice/originalPrice must carry the tax-exclusive netunit value
          // to match every live cart-item build path (cart.js), otherwise the printed Price
          // shows tax-inclusive while tax is also shown separately, and Price×Qty stops
          // matching the (correctly tax-exclusive) line Amount.
          //
          // realPrice/grossunit are different: BUG-1558 requires the receipt's unit-Price
          // column to show the pre-discount standard price, not the discounted one (the
          // discount total is called out separately via grossTotal). See preDiscountUnitPrice
          // above for why this isn't simply pro.grossstd.
          realPrice: preDiscountUnitPrice,
          listPrice: pro.netunit || 0,
          sunitprice: pro.netunit || 0,
          returnQty: null,
          originalPrice: pro.netunit || 0,
          productSegment: "",
          salePrice: pro.netunit || 0,
          // grossunit is the unit-price field print templates bind to (mirrors every live
          // cart-item build path, e.g. cart.js/useCartOperations.js) — was missing entirely
          // for order-history/aggregator reconstructed lines, leaving the printed Price blank.
          // See preDiscountUnitPrice above for why this isn't simply pro.grossstd.
          grossunit: preDiscountUnitPrice,
          stock: 0,
          tax: pro.tax?.csTaxID || null,
          taxCategory: "",
          taxName: pro.tax?.name || "",
          taxFlag: "N",
          taxAmount: pro.linetax || 0,
          taxRate: pro.tax?.rate || 0,
          uom: pro.uom?.csUomId || null,
          uom_name: pro.uom?.name || "",
          addNewLine: "N",
          customAttributes: [],
          showAddons: false,
          foodType: "",
          type: "",
          newCustomAttributes: [],
          priceList: [],
          addons: pro.addons || [],
          isDecimalQty: isDecimal,
          isQtyDesimal: uomData[0]?.stdprecision ?? (pro.uom?.decimal === "Y" ? 2 : 0),
          upc: pro.product?.upc || null,
          value: pro.product?.value || null,
          weight: pro.qty || 0,
          order: "N",
          productionCenter: "",
          shortDescription: pro.product?.shortDescription || "",
          hsncode: pro.product?.hsn || null,
          csBunitId: null,
          mProductCategoryId: null,
          productManufacturerId: null,
          productBrandId: null,
          productBrandName: null,
          productCategoryName: "",
          productAddons: [],
          batchedProduct: false,
          batchedForSale: false,
          batchedForStock: false,
          multiPrice: [],
          shelfLife: 0,
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          layAway: orderData.layAway || "N",
          grossstd: pro.grossstd || 0,
          linestd: (pro.grossstd || 0) * (pro.qty || 0),
          uomData: uomData,
        };
      });

      const paymentMethods = [];
      let change = 0;
      let paidAmount = 0;
      orderData?.finReceiptPlan?.[0]?.finReceiptPlanDetails?.forEach((ele) => {
        if (ele.amount > 0) {
          paymentMethods.push({
            cWRPaymentMethodID: ele.cwrPaymentmethod?.cWRPaymentMethodID || null,
            finFinancialAccountId: ele.cwrPaymentmethod?.finFinancialAccountId || null,
            finPaymentmethodId: ele.cwrPaymentmethod?.finPaymentmethodId || null,
            integratedPayment: ele.cwrPaymentmethod?.integratedPayment || false,
            isloyalty: ele.cwrPaymentmethod?.isloyalty || false,
            paymentProvider: ele.cwrPaymentmethod?.paymentProvider || "Unknown",
            amount: parseFloat(ele.amount.toFixed(2)),
            advancePayment: parseFloat(ele.amount.toFixed(2)),
            transactionId: ele.transactionId || "",
            cardType: ele.cardType || "",
            cardNo: ele.cardNo || "",
            transactionTime: ele.transactionTime ? dayjs(ele.transactionTime).format("DD-MM-YYYY HH:mm:ss") : "",
            authorizationCode: ele.cwrPaymentmethod?.authorizationCode || "",
          });
          paidAmount += ele.amount;
        } else if (ele.amount < 0) {
          change = ele.amount;
        }
      });
      const totalStd = lineItems?.reduce((sum, item) => sum + (item.linestd || 0), 0);
      // TSK-2956: additionalCharges may be null/undefined/empty depending on order source
      const packingCharge = Array.isArray(orderData.additionalCharges)
        ? orderData.additionalCharges.reduce((sum, charge) => sum + (Number(charge?.amount) || 0), 0)
        : 0;
      transformedOrder = {
        change,
        items: lineItems || [],
        cardPaymentData: {},
        originalPrice: orderData.grosstotal || 0,
        customer: orderData.cwrB2cCustomer || "",
        documentno: orderData.documentno,
        b2bOrder: orderData?.b2bOrder || "N",
        externalId: orderData?.externalId || null,
        externalId2: orderData?.externalId2 || null,
        total: parseFloat((orderData.grosstotal || 0).toFixed(precision)),
        discount: orderData.discAmount || 0,
        tax: orderData.taxamt || 0,
        deliveryDate: orderData.deliveryDate ? dayjs(orderData.deliveryDate).format("DD-MM-YYYY HH:mm:ss") : null,
        orderTime: orderData.orderTime ? dayjs(orderData.orderTime).format("DD-MM-YYYY HH:mm:ss") : null,
        orderDate: orderData.dateordered,
        totalQty: orderData.cwrProductQty,
        payments: [],
        paymentData: paymentMethods || [],
        roundOff: 0,
        sOrderID: orderData.sOrderID,
        key: orderData.documentno,
        salesHistory: "Y",
        status: "Success",
        isSynced: 0,
        creditAmount: 0,
        createdBy: orderData.createdby || "",
        layAway: orderData.layAway || "N",
        isReturn: orderData.isReturn || "N",
        paid: paidAmount || 0,
        advancePayment: paidAmount || 0,
        redemptionPoints: 0,
        accumulationPoints: 0,
        saleType: orderData.saleType || {},
        invoiceNo: orderData.invoiceNo || "",
        invoiceId: orderData.invoiceId || "",
        invoiceStatus: orderData.invoiceStatus || "",
        invoiceDate: orderData.invoiceDate || "",
        // TSK-4875: the Aggregator screen uses this object as selectedOrder right after Accept.
        isBilled: orderData?.isBilled || "N",
        tableNo: orderData.tablename || "",
        channel: orderData.saleType?.name || "Unknown",
        metaData: orderData?.metaData,
        quickOrder: (orderData?.metaData?.find((md) => md.key === "Instant Order")?.value === "true" && true) || null,
        orderOTP: orderData?.metaData?.find((md) => md.key === "Order OTP")?.value || null,
        orderInstructions: orderData?.metaData?.find((md) => md.key === "Instructions")?.value || null,
        channelId: `${orderData?.externalId?.slice(-4)}`,
        referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
        userName: userData?.user,
        tillName: tillValue?.name,
        subTotal: parseFloat((orderData.grosstotal - orderData.discAmount - orderData.taxamt || 0).toFixed(precision)),
        itemsSummery: itemSummery,
        amountInWords: numberToWords(orderData.grosstotal || 0),
        groupedItemsByTaxRate: groupedItemsByTaxRate,
        totalStd: parseFloat(totalStd.toFixed(precision)),
        packingCharge: parseFloat(packingCharge.toFixed(precision)),
        additionalCharges: orderData.additionalCharges || [],
      };

      // Call CWKOTPrinter if forPrinting is true and transformedOrder is not null
      console.debug("[ORDER-ACCEPT-DIAG] print gate reached", { order_id, hasTransformedOrder: !!transformedOrder, manualprint, disableAggregatorInvoicePrint, disableAggregatorKOTPrint });
      if (transformedOrder && !manualprint) {
        try {
          // Local dedup already claimed above, before the fetch (BUG-1971). This is a
          // server-truth check only -- e.g. another client/session already printed it.
          const isServerPrinted = orderData?.print === "Y";
          console.debug("[ORDER-ACCEPT-DIAG] print guard check", { order_id, isServerPrinted });

          if (isServerPrinted) {
            console.warn(`[PrintGuard] Duplicate print prevented (server already printed) for Order: ${order_id}`);
            return;
          }

          // Perform validity check before printing
          if (!validityCheck(orderData, order_id)) {
            console.debug("[ORDER-ACCEPT-DIAG] validityCheck failed, aborting print", { order_id });
            message.warning(`Order ${orderData?.documentno || order_id}: print skipped because the order timestamp looks stale. If this was a live order, reprint it manually from the Aggregator screen.`, 6);
            return;
          }
          // BUG-1971: invoice and KOT are independent outputs, gated by their own flags --
          // previously an if/else-if made them mutually exclusive, so with both flags at
          // their default (false = enabled), only the invoice ever printed and KOT never did.
          // Each is also now wrapped in its own try/catch: printController() used to be able
          // to throw synchronously (e.g. a till record with a null printTemplate/hardwareController
          // relation) and, sharing one try block with the KOT call that followed it in source
          // order, silently take KOT printing down with it -- "no print at all" with only a
          // console.error, no cashier-visible signal, no retry.
          let invoicePrinted = false;
          let kotPrinted = false;
          if (!disableAggregatorInvoicePrint) {
            try {
              console.debug("[ORDER-ACCEPT-DIAG] calling printController", { order_id });
              printController(transformedOrder);
              invoicePrinted = true;
              console.debug("[ORDER-ACCEPT-DIAG] printController call returned without throwing", { order_id });
            } catch (invoiceError) {
              console.error(`[ORDER-ACCEPT-DIAG] Invoice print failed for order ${order_id}:`, invoiceError, invoiceError?.stack);
              showPrintFailure({ kind: "onlineOrder", ref: orderData?.documentno || order_id, label: "Online order bill", cause: "unexpected", detail: invoiceError?.message });
            }
          }
          if (!disableAggregatorKOTPrint) {
            try {
              console.debug("[ORDER-ACCEPT-DIAG] calling CWKOTPrinter", { order_id });
              CWKOTPrinter(transformedOrder, true);
              kotPrinted = true;
            } catch (kotError) {
              console.error(`[ORDER-ACCEPT-DIAG] KOT print failed for order ${order_id}:`, kotError, kotError?.stack);
              showPrintFailure({ kind: "onlineOrder", ref: orderData?.documentno || order_id, label: "Online order KOT", cause: "unexpected", detail: kotError?.message });
            }
          }
          // Only mark the order server-side "printed" once every output that was actually
          // enabled for it succeeded -- marking it on a failed attempt would make a later
          // legitimate retry look like a duplicate via the isServerPrinted check above.
          const printSucceeded = (disableAggregatorInvoicePrint || invoicePrinted) && (disableAggregatorKOTPrint || kotPrinted);
          if (printSucceeded) {
            const updateResult = await updateOfdStatus({
              sOrderID: order_id,
              newStatus: selectedOrderStatus,
              printFlag: "Y",
            });
            if (!updateResult.success) {
              console.error(`Failed to update order ${order_id} with print: "N"`, updateResult.message);
            }
          }
        } catch (printError) {
          console.error(`[ORDER-ACCEPT-DIAG] Failed to print order ${order_id}:`, printError, printError?.stack);
          showPrintFailure({ kind: "onlineOrder", ref: orderData?.documentno || order_id, cause: "unexpected", detail: printError?.message });
        }
      }
    }

    const paymentMethods =
      orderData.finReceiptPlan?.[0]?.finReceiptPlanDetails?.map((detail) => ({
        name: detail.cwrPaymentmethod.paymentProvider || "Unknown",
        amount: detail.amount,
      })) || [];

    const lineItems = orderData.line.map((item) => ({
      name: item.product.name,
      quantity: item.qty,
      discount: item.discount || "0.00",
      tax: item.linetax || "0.00",
      // price must be per-unit since consumers (Aggregator OrderDetails display, posOrderProcessor1
      // sync payload) pair it with quantity themselves; linegross is already the qty-multiplied
      // line total and doubled the effective quantity when multiplied again (BUG-1909).
      price: item.netunit ?? (item.qty > 0 ? (item.linenet || 0) / item.qty : 0),
      linenet: item.linenet,
      addons: item.addons || [],
      // TSK-3229: line/product identity, absent before, needed to run a stock check per item
      // and to identify which order line a "Modify Items" substitution targets.
      sOrderlineID: item.sOrderlineID,
      productId: item.product?.mProductId || null,
      value: item.product?.value || null,
    }));

    const detailedOrder = {
      sOrderID: orderData.sOrderID,
      orderNo: orderData.documentno,
      channel: orderData.saleType?.name || "Unknown",
      externalId: orderData?.externalId,
      externalId2: orderData?.externalId2,
      status: selectedOrderStatus || orderData.ofdStatus || "NEW",
      amount: orderData.grosstotal,
      paymentMode: paymentMethods.length > 0 ? paymentMethods[0].name : "Unknown",
      eta: "30 Min",
      customerName: orderData.cwrB2cCustomer?.name || "Unknown",
      customerPhone: orderData.cwrB2cCustomer?.mobileNo || "N/A",
      customerEmail: orderData.cwrB2cCustomer?.email || "N/A",
      customerAddress: orderData.csbUnitLocation?.fulladdress || "N/A",
      items: lineItems,
      orderTime: orderData.orderTime,
      tax: orderData.taxamt,
      discount: orderData.discAmount,
      paid: orderData.paid,
      // TSK-4875: "Y" once shipment/invoice exist (Generate Bill or Dispatch fallback).
      isBilled: orderData?.isBilled || "N",
      additionalCharges: orderData?.additionalCharges || [],
      metaData: orderData?.metaData || null,
      saleType: orderData?.saleType || {},
    };

    return forPrinting ? transformedOrder : manualprint ? { transformedOrder, detailedOrder } : detailedOrder;
  } catch (error) {
    console.error(`Error fetching order details for order_id: ${order_id}`, error);
    return null;
  }
};

export { getSalesHistoryDetails };
